/**
 * @file Utilitaires de manipulation de couleurs (contraste, conversions, variations, dégradés).
 * @module packages.common.utils.colorUtils
 *
 * Fonctions pures et agnostiques du domaine, réutilisables dans toute l'application :
 * choix d'une couleur de texte lisible selon le fond (luminance WCAG 2.1), conversions
 * hex/RGB, éclaircissement/assombrissement et génération de dégradés CSS. Toutes les
 * fonctions tolèrent une entrée invalide en renvoyant une valeur de repli sûre plutôt
 * qu'en levant une exception.
 */

/**
 * Détermine la couleur de texte la plus lisible selon la couleur de fond.
 *
 * Combine la luminance relative WCAG 2.1 (avec correction gamma) et la saturation :
 * les couleurs très saturées (or, orange) conservent un texte blanc plus longtemps
 * grâce à un seuil relevé. Renvoie du noir/gris foncé sur les fonds clairs, du blanc
 * sur les fonds sombres ou moyens.
 *
 * @param {string} backgroundColor - Couleur au format hexadécimal (avec ou sans #)
 * @return {string} '#161616' (noir) sur fond très clair, '#2D3436' (gris anthracite)
 *   en zone intermédiaire, '#FFFFFF' (blanc) sinon ou en cas d'entrée invalide
 * @example
 * getContrastTextColor('#FFD700'); // '#2D3436' (or saturé, zone intermédiaire)
 * getContrastTextColor('#1A1A1A'); // '#FFFFFF' (fond sombre)
 * getContrastTextColor('invalide'); // '#FFFFFF' (repli)
 */
export const getContrastTextColor = (backgroundColor) => {
  if (!backgroundColor || typeof backgroundColor !== 'string') {
    return '#FFFFFF';
  }

  // Retire le # si présent
  const color = backgroundColor.charAt(0) === '#' ? backgroundColor.substring(1, 7) : backgroundColor;

  // Validation basique
  if (color.length !== 6) {
    return '#FFFFFF';
  }

  try {
    // Convertit les valeurs hex en RGB
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);

    // Luminance relative WCAG 2.1 (canaux linéarisés par correction gamma)
    const sR = r / 255;
    const sG = g / 255;
    const sB = b / 255;

    const rLin = sR <= 0.03928 ? sR / 12.92 : Math.pow((sR + 0.055) / 1.055, 2.4);
    const gLin = sG <= 0.03928 ? sG / 12.92 : Math.pow((sG + 0.055) / 1.055, 2.4);
    const bLin = sB <= 0.03928 ? sB / 12.92 : Math.pow((sB + 0.055) / 1.055, 2.4);

    const luminance = 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;

    // Saturation : sert à relever le seuil pour les couleurs vives (or, orange)
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const saturation = max === 0 ? 0 : (max - min) / max;

    let threshold = 0.4;

    // Couleurs saturées : conserver le texte blanc plus longtemps
    if (saturation > 0.3) {
      threshold = 0.5;
    }

    // Fonds très clairs : noir
    if (luminance > 0.8) {
      return '#161616';
    }

    // Zone intermédiaire : gris anthracite (plus doux que le noir pur)
    if (luminance > threshold && luminance <= 0.8) {
      return '#2D3436';
    }

    // Fonds sombres et moyens : blanc
    return '#FFFFFF';
  } catch (error) {
    return '#FFFFFF';
  }
};

/**
 * Détermine la couleur de texte par luminosité perçue, avec seuil ajustable.
 *
 * Variante allégée de `getContrastTextColor` reposant sur la luminosité perçue
 * (pondération 299/587/114) plutôt que sur la luminance WCAG. Le seuil contrôle
 * la permissivité : plus il est élevé, plus le texte foncé est réservé aux fonds clairs.
 *
 * @param {string} backgroundColor - Couleur au format hexadécimal
 * @param {number} [threshold=200] - Seuil de luminosité (plus haut = plus permissif)
 * @return {string} '#2D3436' (gris foncé) au-dessus du seuil, '#FFFFFF' (blanc) sinon
 *   ou en cas d'entrée invalide
 * @example
 * getContrastTextColorSimple('#FFFFFF'); // '#2D3436'
 * getContrastTextColorSimple('#000000'); // '#FFFFFF'
 */
export const getContrastTextColorSimple = (backgroundColor, threshold = 200) => {
  if (!backgroundColor || typeof backgroundColor !== 'string') {
    return '#FFFFFF';
  }

  const color = backgroundColor.charAt(0) === '#' ? backgroundColor.substring(1, 7) : backgroundColor;

  if (color.length !== 6) {
    return '#FFFFFF';
  }

  try {
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);

    // Luminosité perçue (pondération standard des canaux)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    return brightness > threshold ? '#2D3436' : '#FFFFFF';
  } catch (error) {
    return '#FFFFFF';
  }
};

/** Palettes de couleurs génériques indexées par clé ('vibrant', 'soft'). */
export const COLOR_PALETTES = {
  vibrant: [
    '#e91e63', // Pink 500 - Rose vif
    '#ff5722', // Deep Orange 500 - Orange rouge
    '#9c27b0', // Purple 500 - Violet
    '#3f51b5', // Indigo 500 - Indigo
    '#009688', // Teal 500 - Teal
    '#795548', // Brown 500 - Marron
    '#607d8b', // Blue Grey 500 - Bleu gris
    '#ff9800', // Orange 500 - Orange
    '#8bc34a', // Light Green 500 - Vert clair
    '#f44336', // Red 500 - Rouge
    '#2196f3', // Blue 500 - Bleu
    '#4caf50', // Green 500 - Vert
  ],
  soft: [
    '#2196f3', // Blue 500 - Bleu principal
    '#f44336', // Red 500 - Rouge vif
    '#4caf50', // Green 500 - Vert nature
    '#ff9800', // Orange 500 - Orange énergique
    '#9e9e9e', // Grey 500 - Gris neutre
    '#673ab7', // Deep Purple 500 - Violet profond
    '#00bcd4', // Cyan 500 - Cyan frais
    '#8bc34a', // Light Green 500 - Vert pomme
    '#ffc107', // Amber 500 - Ambre doré
    '#e91e63', // Pink 500 - Rose moderne
    '#795548', // Brown 500 - Marron terre
    '#607d8b', // Blue Grey 500 - Bleu ardoise
  ],
};

/**
 * Attribue une couleur à un élément depuis une palette, de façon cyclique.
 *
 * Une couleur personnalisée valide prend toujours le pas sur la palette. À défaut,
 * l'index est ramené dans les bornes de la palette par modulo, ce qui garantit
 * une couleur stable et répétée au-delà de la taille de la palette.
 *
 * @param {string} paletteKey - Clé de la palette ('vibrant', 'soft')
 * @param {number} index - Position de l'élément dans la liste
 * @param {string|null} [customColor=null] - Couleur personnalisée prioritaire
 * @return {string} Code couleur hexadécimal, ou gris '#9e9e9e' si la palette est introuvable
 * @example
 * assignAutoColor('vibrant', 0); // '#e91e63'
 * assignAutoColor('vibrant', 12); // '#e91e63' (cycle sur 12 couleurs)
 * assignAutoColor('soft', 3, '#000000'); // '#000000' (couleur personnalisée prioritaire)
 */
export function assignAutoColor(paletteKey, index, customColor = null) {
  if (customColor && isValidHexColor(customColor)) {
    return customColor;
  }

  const palette = COLOR_PALETTES[paletteKey];

  if (!palette || !Array.isArray(palette)) {
    console.warn(`Palette introuvable: ${paletteKey}`);
    return '#9e9e9e';
  }

  // Attribution cyclique : l'index revient au début une fois la palette épuisée
  return palette[index % palette.length];
}

/**
 * Vérifie qu'une chaîne est un code couleur hexadécimal valide (3 ou 6 chiffres).
 *
 * @param {string} color - Code couleur à valider
 * @return {boolean} `true` pour un hex à 3 ou 6 caractères précédé de #, `false` sinon
 * @example
 * isValidHexColor('#fff'); // true
 * isValidHexColor('#e91e63'); // true
 * isValidHexColor('rgb(0,0,0)'); // false
 */
export function isValidHexColor(color) {
  if (typeof color !== 'string') return false;
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
}

/**
 * Convertit une couleur hexadécimale en composantes RGB.
 *
 * @param {string} hex - Couleur au format hexadécimal (#RRGGBB)
 * @return {{r: number, g: number, b: number}} Composantes RGB (chacune 0-255)
 * @example
 * hexToRgb('#FF8000'); // { r: 255, g: 128, b: 0 }
 */
export const hexToRgb = (hex) => {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return {r, g, b};
};

/**
 * Convertit des composantes RGB en format hexadécimal.
 *
 * Chaque composante est arrondie puis bornée à l'intervalle [0, 255], ce qui
 * permet de passer en entrée des valeurs hors bornes issues d'un calcul.
 *
 * @param {number} r - Composante rouge (0-255)
 * @param {number} g - Composante verte (0-255)
 * @param {number} b - Composante bleue (0-255)
 * @return {string} Couleur au format hexadécimal #RRGGBB
 * @example
 * rgbToHex(255, 128, 0); // '#ff8000'
 * rgbToHex(300, -10, 0); // '#ff0000' (valeurs bornées)
 */
export const rgbToHex = (r, g, b) => {
  return '#' +
    Math.max(0, Math.min(255, Math.round(r))).toString(16).padStart(2, '0') +
    Math.max(0, Math.min(255, Math.round(g))).toString(16).padStart(2, '0') +
    Math.max(0, Math.min(255, Math.round(b))).toString(16).padStart(2, '0');
};

/**
 * Assombrit une couleur d'un certain pourcentage (rapproche chaque canal du noir).
 *
 * @param {string} hex - Couleur au format hexadécimal
 * @param {number} [percentage=15] - Pourcentage d'assombrissement (0-100)
 * @return {string} Couleur assombrie au format hexadécimal, ou l'entrée telle quelle si invalide
 * @example
 * darkenColor('#808080', 50); // '#404040'
 */
export const darkenColor = (hex, percentage = 15) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return hex;
  }
  const {r, g, b} = hexToRgb(hex);
  const factor = 1 - (percentage / 100);
  return rgbToHex(r * factor, g * factor, b * factor);
};

/**
 * Éclaircit une couleur d'un certain pourcentage (rapproche chaque canal du blanc).
 *
 * @param {string} hex - Couleur au format hexadécimal
 * @param {number} [percentage=15] - Pourcentage d'éclaircissement (0-100)
 * @return {string} Couleur éclaircie au format hexadécimal, ou l'entrée telle quelle si invalide
 * @example
 * lightenColor('#808080', 50); // '#bfbfbf'
 */
export const lightenColor = (hex, percentage = 15) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return hex;
  }
  const {r, g, b} = hexToRgb(hex);
  const factor = percentage / 100;
  return rgbToHex(
      r + (255 - r) * factor,
      g + (255 - g) * factor,
      b + (255 - b) * factor,
  );
};

/**
 * Crée un dégradé CSS linéaire entre une couleur de base et une variation calculée.
 *
 * La seconde teinte est dérivée de `primaryColor` par assombrissement ou
 * éclaircissement selon `useDarkerStart`, qui détermine aussi l'ordre des couleurs
 * (teinte foncée en début de dégradé quand activé).
 *
 * @param {string} primaryColor - Couleur primaire au format hexadécimal
 * @param {Object} [options] - Options de dégradé
 * @param {number} [options.darkenAmount=15] - Intensité de la variation en pourcentage
 * @param {number} [options.angle=45] - Angle du dégradé en degrés
 * @param {number} [options.startPosition=30] - Position de départ en pourcentage
 * @param {number} [options.endPosition=90] - Position de fin en pourcentage
 * @param {boolean} [options.useDarkerStart=true] - Démarrer sur la teinte assombrie
 * @return {string|null} Chaîne CSS `linear-gradient(...)`, ou null si la couleur est invalide
 * @example
 * createGradientFromColor('#2196f3');
 * // 'linear-gradient(45deg, #1c80cf 30%, #2196f3 90%)'
 */
export const createGradientFromColor = (primaryColor, options = {}) => {
  if (!primaryColor || typeof primaryColor !== 'string' || !primaryColor.startsWith('#')) {
    return null;
  }

  const {
    darkenAmount = 15,
    angle = 45,
    startPosition = 30,
    endPosition = 90,
    useDarkerStart = true,
  } = options;

  const secondaryColor = useDarkerStart ?
    darkenColor(primaryColor, darkenAmount) :
    lightenColor(primaryColor, darkenAmount);

  const startColor = useDarkerStart ? secondaryColor : primaryColor;
  const endColor = useDarkerStart ? primaryColor : secondaryColor;

  return `linear-gradient(${angle}deg, ${startColor} ${startPosition}%, ${endColor} ${endPosition}%)`;
};
